import { batch } from "react-redux";

import { DeleteColors, GetColors } from "../../../connection/colors";
import {
	AddNewSession,
	DeleteSession,
	DuplicateSession,
	LoadEvent,
	SaveEvent,
	UpdateSessionInfo,
	UpdateSessionCardInfo,
	PublishEvent,
	PublishEventPreview,
	UpdateRegistrationSettings,
	AddPostRegisterHomePageModuleToEvent,
	AddHomepageToEvent,
	CheckEventPublishedStatus,
	UpdateEventFavicon,
	DeleteEventFavicon,
	SetSocialSettings,
	ResetRegistrations,
	UpdateEventSettings,
	UpdateEventPreviewPassword,
	SetupEventPreview,
	AddLeaderboardNavItem,
	UpdateSessionFeedbackSurveyText
} from "../../../connection/create-event";
import { DeleteFonts, GetFonts } from "../../../connection/fonts";
import { GetTemplates } from "../../../connection/templates";
import { Action, AsyncAction } from "../../../types/actions";
import {
	BrandliveEvent,
	Color,
	CreateSession,
	DesignShape,
	DesignStyle,
	FontPack,
	HomepageFooter,
	HomepageHeader,
	IHomepageMainNavItems,
	IHomepageProfileNavItems,
	PageModule,
	RegistrationQuestion,
	Requirement,
	Session,
	Template,
	GateTypes,
	RegistrationStep,
	RegistrationStepType,
	Registration,
	SessionTabsLayout,
	TicketingSet,
	TranslateString,
	BLAdmin,
	EventHomepage,
	EventNavbar,
	CustomPage,
	SocialSettings,
	ProfileDisplayType,
	EventSettings,
	DirectoryFilter,
	IEventChatChannel,
	IScheduledEmail,
	IInsertScheduledEmailRequest,
	IEventTag,
	ChannelCustomizedIcon,
	IFeedbackSurveyText,
	MarketingOptInTypes,
} from "../../../types/working-model";
import { GetEmptyEvent } from "../../utils/create-event";
import { getEvents, UPDATE_EVENTS_NAME } from "./events";
import { GetGlobalCurrencies } from "../../../connection/integrations";
import { CreateScheduledEmail, DeleteScheduledEmail, EditScheduledEmail, GetChannelScheduledEmails, GetEventScheduledEmails } from "../../../connection/scheduled-emails";
import { AppThunk } from "../../reducers/use-typed-selector";
import { UniqueIdentifier } from "@dnd-kit/core";
import { DeleteCustomPage } from "../../../connection/custom-page";

export const CREATE_NEW_EVENT = "CREATE_NEW_EVENT";
export function createNewEvent(channel: number, template: Template): Action {
	return {
		type: CREATE_NEW_EVENT,
		payload: GetEmptyEvent(channel, template)
	};
}

export const SET_EVENT_NAME = "SET_EVENT_NAME";
export function setEventName(name: string): Action {
	return {
		type: SET_EVENT_NAME,
		payload: name
	};
}

export const LOAD_TEMPLATES = "LOAD_TEMPLATES";
export function loadTemplates(channel: number, token: string): AsyncAction {
	return {
		type: LOAD_TEMPLATES,
		promise: GetTemplates(channel, token)
	};
}

export const ADD_EVENT_TEMPLATE = "ADD_EVENT_TEMPLATE";
export function addEventTemplate(template: Template): Action {
	return {
		type: ADD_EVENT_TEMPLATE,
		payload: template,
	};
}

export const UPDATE_EVENT_TEMPLATE = "UPDATE_EVENT_TEMPLATE";
export function updateEventTemplate(template: Template): Action {
	return {
		type: UPDATE_EVENT_TEMPLATE,
		payload: template,
	};
}

export const REMOVE_CUSTOM_TEMPLATES = "REMOVE_CUSTOM_TEMPLATES";
export function removeEventCustomTemplates(templates: number[]): Action {
	return {
		type: REMOVE_CUSTOM_TEMPLATES,
		payload: templates,
	};
}

interface ICreateNewEventSession {
	session: CreateSession;
	workingEvent: BrandliveEvent;
	active_channel: number;
	token: string;
	mulitpleSessions?: 'multi';
	user: BLAdmin;
}

// we're using redux thunk here to be able to wait for multiple calls before dispatching,
// otherwise (with redux pack and the way we're handling updates) we end up with time races between dispatches and things can be overwritten
export const ADD_HOMEPAGE_TO_WORKING_EVENT = "ADD_HOMEPAGE_TO_WORKING_EVENT";
export const ADD_NEW_SESSION_TO_STORE = "ADD_NEW_SESSION_TO_STORE";
export const CREATE_NEW_EVENT_SESSION = "CREATE_NEW_EVENT_SESSION";
export const createNewEventSession = (data: ICreateNewEventSession, cb?: () => void): AppThunk => async (dispatch): Promise<void> => {
	try {
		const {
			session,
			workingEvent,
			active_channel,
			token,
			mulitpleSessions,
			user
		} = data;
		if (workingEvent?.event) {
			const newSession = await AddNewSession(session, workingEvent.event, workingEvent.uuid, active_channel, token);
			let homepage: EventHomepage;
			if (!workingEvent?.homepage) {
				homepage = await AddHomepageToEvent(workingEvent.uuid, token, mulitpleSessions);
			}
			// dispatch all calls to update redux in various places
			batch(() => {
				if (homepage) {
					dispatch({
						type: ADD_HOMEPAGE_TO_WORKING_EVENT,
						payload: homepage,
					});
				}
				dispatch({
					type: ADD_NEW_SESSION_TO_STORE,
					payload: newSession,
				});
				dispatch(getEvents(user.active_channel, token));
			});
		}
	} catch (e) {
		console.error(e);
	} finally {
		if (cb) cb();
	}
};

export const CREATE_NEW_SESSION = "CREATE_NEW_SESSION";
export function createNewSession(newSession: CreateSession): Action {
	return {
		type: CREATE_NEW_SESSION,
		payload: newSession
	};
}

export const ADD_NEW_SESSION = "ADD_NEW_SESSION";
export function addNewSession(
	newSession: CreateSession,
	event: number,
	eventUuid: string,
	channel: number,
	token: string,
	cb?: () => void,
): AsyncAction {
	return {
		type: ADD_NEW_SESSION,
		promise: AddNewSession(newSession, event, eventUuid, channel, token, cb)
	};
}

export const DUPLICATE_SESSION = "DUPLICATE_SESSION";
export function duplicateSession(
	session: Session,
	event: number,
	eventUuid: string,
	token: string
): AsyncAction {
	return {
		type: DUPLICATE_SESSION,
		promise: DuplicateSession(session, event, eventUuid, token)
	};
}

export const DELETE_SESSION = "DELETE_SESSION";
export function deleteSession(
	sessionID: number,
	eventUuid: string,
	token: string
): AsyncAction {
	return {
		type: DELETE_SESSION,
		promise: DeleteSession(sessionID, eventUuid, token)
	};
}

export const REMOVE_SESSION = "REMOVE_SESSION";
export function removeSessionFromCreateEventReducer(sessionID: number, eventUuid: string): Action {
	return {
		type: REMOVE_SESSION,
		payload: { session: sessionID, eventUuid }
	};
}

export const REMOVE_EVENT = "REMOVE_EVENT";
export function removeEventFromCreateEventReducer(eventUuid: string): Action {
	return {
		type: REMOVE_EVENT,
		payload: eventUuid
	};
}

export const UPDATE_SESSION_INFO = "UPDATE_SESSION_INFO";
export function updateSessionInfo(
	session: Session,
	token: string
): AsyncAction {
	return {
		type: UPDATE_SESSION_INFO,
		promise: UpdateSessionInfo(session, token)
	};
}

// stripped down alternative to updateSessionInfo for quicker processing of the basics
// doesn't reload the event after saving, doesn't deal with session modules, only updates limited session info (currently title, description, image)
export const UPDATE_SESSION_CARD = "UPDATE_SESSION_CARD";
export function updateSessionCard(
	session: Session,
	token: string
): AsyncAction {
	return {
		type: UPDATE_SESSION_CARD,
		promise: UpdateSessionCardInfo(session, token)
	};
}

export const SET_WORKING_EVENT = "SET_WORKING_EVENT";
export function setWorkingEvent(newEvent: BrandliveEvent): Action {
	return {
		type: SET_WORKING_EVENT,
		payload: newEvent
	};
}

export const SET_SELECTED_EVENT = "SET_SELECTED_EVENT";
export function setSelectedEvent(event: BrandliveEvent): Action {
	return {
		type: SET_SELECTED_EVENT,
		payload: event
	};
}

export const CLEAR_SELECTED_EVENT = "CLEAR_SELECTED_EVENT";
export function clearSelectedEvent(): Action {
	return {
		type: CLEAR_SELECTED_EVENT,
		payload: null
	};
}

export const CLEAR_WORKING_EVENT = "CLEAR_WORKING_EVENT";
export function clearWorkingEvent(): Action {
	return {
		type: CLEAR_WORKING_EVENT,
		payload: null
	};
}

export const CLEAR_PUBLISHED_URL = "CLEAR_PUBLISHED_URL";
export function clearPublishedUrl(): Action {
	return {
		type: CLEAR_PUBLISHED_URL,
		payload: null,
	};
}

export const BLUR_EDITOR = "BLUR_EDITOR";
export function blurEditor(shouldBlur: boolean): Action {
	return {
		type: BLUR_EDITOR,
		payload: { shouldBlur },
	};
}

export const MODAL_IN_EDITOR = "MODAL_IN_EDITOR";
export function modalInEditor(modalInEditor: boolean): Action {
	return {
		type: MODAL_IN_EDITOR,
		payload: modalInEditor
	};
}

export const LOAD_WORKING_EVENT = "LOAD_WORKING_EVENT";
export function loadWorkingEvent(uuid: string, token: string): AsyncAction {
	return {
		type: LOAD_WORKING_EVENT,
		promise: LoadEvent(uuid, token),
	};
}

export const IS_UPDATING_SETTINGS = 'IS_UPDATING_SETTINGS';
export function isUpdatingSettings(payload: boolean): Action {
	return {
		type: IS_UPDATING_SETTINGS,
		payload,
	};
}

// refactored this to use thunk instead of adding `UPDATE_EVENT_SETTINGS` to the reducer save list
// because there are a few places that rely on the saving isUpdatingSettings and isSavingEvent and it's just
// easier to update this one one action rather than all the files that use the functionality
export const UPDATE_EVENT_SETTINGS = "UPDATE_EVENT_SETTINGS";
export function updateEventSettings(token: string, event_uuid: string, settings: EventSettings): AppThunk {
	return async function(dispatch): Promise<void> {
		try {
			batch(() => {
				dispatch(isUpdatingSettings(true));
				dispatch(isSavingEvent(true));
			});
			const result = await UpdateEventSettings(token, event_uuid, settings);
			dispatch({
				type: UPDATE_EVENT_SETTINGS,
				payload: result,
			});
		} catch (e) {
			console.error(e);
		} finally {
			batch(() => {
				dispatch(isUpdatingSettings(false));
				dispatch(isSavingEvent(false));
			});
		}
	};
}

export const SET_REGISTRATION_ON = "SET_REGISTRATION_ON";
export function setRegistrationOn(on: boolean): Action {
	return {
		type: SET_REGISTRATION_ON,
		payload: on
	};
}

export const SET_REGISTRATION_TYPE = "SET_REGISTRATION_TYPE";
export function setRegistrationType(type: number): Action {
	return {
		type: SET_REGISTRATION_TYPE,
		payload: type
	};
}

export const UPDATE_REGISTRATION_SETTINGS = "UPDATE_REGISTRATION_SETTINGS";
export function updateRegistrationSettings(token: string, event_uuid: string, settings: Registration): AsyncAction {
	return {
		type: UPDATE_REGISTRATION_SETTINGS,
		promise: UpdateRegistrationSettings(token, event_uuid, settings)
	};
}

export const ADD_REGISTRATION_REQUIREMENT = "ADD_REGISTRATION_REQUIREMENT";
export function addRegistrationRequirement(type: GateTypes, newRequirement: Requirement): Action {
	return {
		type: ADD_REGISTRATION_REQUIREMENT,
		payload: {
			type,
			newRequirement
		}
	};
}

export const REMOVE_REGISTRATION_REQUIREMENT = "REMOVE_REGISTRATION_REQUIREMENT";
export function removeRegistrationRequirement(type: GateTypes, passcodeListIndex?: number): Action {
	return {
		type: REMOVE_REGISTRATION_REQUIREMENT,
		payload: {
			type,
			passcodeListIndex
		}
	};
}

export const UPDATE_REGISTRATION_REQUIREMENT =
	"UPDATE_REGISTRATION_REQUIREMENT";
export function updateRegistrationRequirement(
	type: GateTypes,
	updatedRequirement: Requirement,
	passcodeListIndex?: number,
): Action {
	return {
		type: UPDATE_REGISTRATION_REQUIREMENT,
		payload: { type, passcodeListIndex, updatedRequirement }
	};
}

export const SET_REGISTRATION_FEATURE = "SET_REGISTRATION_FEATURE";
export function setRegistrationFeature(feature: string, on: boolean): Action {
	return {
		type: SET_REGISTRATION_FEATURE,
		payload: { feature, on }
	};
}

export const SET_CUSTOM_PASSCODE_LABEL = "SET_CUSTOM_PASSCODE_LABEL";
export function setCustomPasscodeLabel(label: TranslateString): Action {
	return {
		type: SET_CUSTOM_PASSCODE_LABEL,
		payload: label
	};
}

export const SET_REJECTED_PASSCODE_MESSAGE = "SET_REJECTED_PASSCODE_MESSAGE";
export function setRejectedPasscodeMessage(label: TranslateString): Action {
	return {
		type: SET_REJECTED_PASSCODE_MESSAGE,
		payload: label
	};
}

export const SET_REGISTRATION_SINGLE_SIGN_ON = "SET_REGISTRATION_SINGLE_SIGN_ON";
export function setRegistrationSingleSignOn(enabled: boolean): Action {
	return {
		type: SET_REGISTRATION_SINGLE_SIGN_ON,
		payload: enabled
	};
}

export const SET_REGISTRATION_SSO_DISPLAY_TOP = "SET_REGISTRATION_SSO_DISPLAY_TOP";
export function setRegistrationSSODisplayTop(isOn: boolean): Action {
	return {
		type: SET_REGISTRATION_SSO_DISPLAY_TOP,
		payload: isOn
	};
}

export const SET_REGISTRATION_SINGLE_SIGN_ON_TYPES = "SET_REGISTRATION_SINGLE_SIGN_ON_TYPES";
export function setRegistrationSingleSignOnTypes(singleSignOns: string[]): Action {
	return {
		type: SET_REGISTRATION_SINGLE_SIGN_ON_TYPES,
		payload: singleSignOns
	};
}

export const TOGGLE_REGISTRATION_QUESTION_REQUIRED =
	"TOGGLE_REGISTRATION_QUESTION_REQUIRED";
export function toggleRegistrationQuestionRequired(
	question: number,
	required: boolean
): Action {
	return {
		type: TOGGLE_REGISTRATION_QUESTION_REQUIRED,
		payload: { question, required }
	};
}

export const SET_REGISTRATION_QUESTIONS = "SET_REGISTRATION_QUESTIONS";
export function setRegistrationQuestions(
	questions: RegistrationQuestion[],
	required_questions: number[]
): Action {
	return {
		type: SET_REGISTRATION_QUESTIONS,
		payload: { questions, required_questions }
	};
}

export const CHOOSE_REGISTRATION_STEP = "CHOOSE_REGISTRATION_STEP";
export function chooseRegistrationStep(step: RegistrationStepType): Action {
	return {
		type: CHOOSE_REGISTRATION_STEP,
		payload: step
	};
}

export const TOGGLE_CUSTOMIZE_FORM_MODAL = "OPEN_CUSTOMIZE_FORM_MODAL";
export function toggleCustomizeFormModal(open: boolean): Action {
	return {
		type: TOGGLE_CUSTOMIZE_FORM_MODAL,
		payload: open
	};
}

export const UPDATE_REGISTRATION_STEPS = "UPDATE_REGISTRATION_STEPS";
export function updateRegistrationSteps(steps: RegistrationStep[]): Action {
	return {
		type: UPDATE_REGISTRATION_STEPS,
		payload: steps
	};
}

export const UPDATE_REQUIRED_REGISTRATION_QUESTIONS = "UPDATE_REQUIRED_REGISTRATION_QUESTIONS";
export function updateRequiredQuestions(requiredQuestions: number[]): Action {
	return {
		type: UPDATE_REQUIRED_REGISTRATION_QUESTIONS,
		payload: requiredQuestions
	};
}

export const LOAD_FONT_PACKS = "LOAD_FONT_PACKS";
export function loadFontPacks(channel: number, token: string): AsyncAction {
	return {
		type: LOAD_FONT_PACKS,
		promise: GetFonts(channel, token)
	};
}

export const SET_SELECTED_FONT = "SET_SELECTED_FONT";
export function setSelectedFont(font: FontPack): Action {
	return {
		type: SET_SELECTED_FONT,
		payload: font
	};
}

export const ADD_NEW_FONT_PACK = "ADD_NEW_FONT_PACK";
export function addNewFontPack(font: FontPack): Action {
	return {
		type: ADD_NEW_FONT_PACK,
		payload: font
	};
}

export const DELETE_FONT_PACKS = "DELETE_FONT_PACKS";
export function deleteFonts(fontPackIDs: number[], token: string, channel: number): AsyncAction {
	return {
		type: DELETE_FONT_PACKS,
		promise: DeleteFonts(fontPackIDs, token, channel)
	};
}

export const LOAD_COLOR_PACKS = "LOAD_COLOR_PACKS";
export function loadColorPacks(token: string, channel: number): AsyncAction {
	return {
		type: LOAD_COLOR_PACKS,
		promise: GetColors(token, channel)
	};
}

export const SET_SELECTED_COLOR = "SET_SELECTED_COLOR";
export function setSelectedColor(color: Color): Action {
	return {
		type: SET_SELECTED_COLOR,
		payload: color
	};
}

export const UPDATE_EMAIL_COLOR = "UPDATE_EMAIL_COLOR";
export function updateEmailColor(color: Color): Action {
	return {
		type: UPDATE_EMAIL_COLOR,
		payload: color
	};
}

export const ADD_NEW_COLOR_PACK = "ADD_NEW_COLOR_PACK";
export function addNewColorPack(color: Color): Action {
	return {
		type: ADD_NEW_COLOR_PACK,
		payload: color
	};
}

export const SAVE_COLOR_PACK = "SAVE_COLOR_PACK";
export function saveColorPack(color: Color): Action {
	return {
		type: SAVE_COLOR_PACK,
		payload: color
	};
}

export const DELETE_COLOR_PACKS = "DELETE_COLOR_PACKS";
export function deleteColors(colorPackIDs: number[], token: string, channel: number): AsyncAction {
	return {
		type: DELETE_COLOR_PACKS,
		promise: DeleteColors(colorPackIDs, token, channel)
	};
}

export const SET_DESIGN_STYLE = "SET_DESIGN_STYLE";
export function setDesignStyle(style: DesignStyle): Action {
	return {
		type: SET_DESIGN_STYLE,
		payload: style
	};
}

export const SET_DESIGN_SHAPE = "SET_DESIGN_SHAPE";
export function setDesignShape(shape: DesignShape): Action {
	return {
		type: SET_DESIGN_SHAPE,
		payload: shape
	};
}

export const SET_DESIGN_COLORS = "SET_DESIGN_COLORS";
export function setDesignColors(colors: Color): Action {
	return {
		type: SET_DESIGN_COLORS,
		payload: colors
	};
}

export const SET_CUSTOM_CSS = "SET_CUSTOM_CSS";
export function setCustomCSS(css: string): Action {
	return {
		type: SET_CUSTOM_CSS,
		payload: css
	};
}

export const TOGGLE_HOMEPAGE_MODULE = "TOGGLE_HOMEPAGE_MODULE";
export function toggleHomepageModule(module: PageModule): Action {
	return {
		type: TOGGLE_HOMEPAGE_MODULE,
		payload: module
	};
}

export const TOGGLE_POST_REGISTER_HOME_MODULE = "TOGGLE_POST_REGISTER_HOME_MODULE";
export function togglePostRegisterPreviewHomeModule(moduleId: number, is_on: boolean): Action {
	return {
		type: TOGGLE_POST_REGISTER_HOME_MODULE,
		payload: { moduleId, is_on },
	};
}

export const SET_HOMEPAGE_MODULES = "SET_HOMEPAGE_MODULES";
export function setHomepageModules(modules: PageModule[]): Action {
	return {
		type: SET_HOMEPAGE_MODULES,
		payload: modules
	};
}

export const RESET_HOMEPAGE_MODULES = "RESET_HOMEPAGE_MODULES";
export function resetHomepageModules(modules: PageModule[]): Action {
	return {
		type: RESET_HOMEPAGE_MODULES,
		payload: modules
	};
}

export const SET_MODULE_ORDER = "SET_MODULE_ORDER";
export function setModuleOrder(newOrder: number[]): Action {
	return {
		type: SET_MODULE_ORDER,
		payload: newOrder
	};
}

export const UPDATE_HOMEPAGE_MODULE = "UPDATE_HOMEPAGE_MODULE";
export function updateHomepageModule(module: PageModule): Action {
	return {
		type: UPDATE_HOMEPAGE_MODULE,
		payload: module
	};
}

export const UPDATE_HOMEPAGE_HEADER = "UPDATE_HOMEPAGE_HEADER";
export function updateHomepageHeader(header: HomepageHeader): Action {
	return {
		type: UPDATE_HOMEPAGE_HEADER,
		payload: header
	};
}

export const UPDATE_HOMEPAGE_MAIN_NAV = "UPDATE_HOMEPAGE_MAIN_NAV";
export function updateHomepageMainNav(navItems: IHomepageMainNavItems[]): Action {
	return {
		type: UPDATE_HOMEPAGE_MAIN_NAV,
		payload: navItems,
	};
}

export const UPDATE_HOMEPAGE_PROFILE_NAV = "UPDATE_HOMEPAGE_PROFILE_NAV";
export function updateHomepageProfileNav(navItems: IHomepageProfileNavItems[]): Action {
	return {
		type: UPDATE_HOMEPAGE_PROFILE_NAV,
		payload: navItems,
	};
}

export const UPDATE_POST_REGISTER_HOME_MODULES = "UPDATE_POST_REGISTER_HOME_MODULES";
export function updatePostRegisterHomeModules(postRegisterHomepageModules: PageModule[]): Action {
	return {
		type: UPDATE_POST_REGISTER_HOME_MODULES,
		payload: postRegisterHomepageModules,
	};
}

export const SET_BLOCKED_DOMAIN_EMAILS = "SET_BLOCKED_DOMAIN_EMAILS";
export function setBlockedDomainEmails(domains: string[]): Action {
	return {
		type: SET_BLOCKED_DOMAIN_EMAILS,
		payload: domains
	};
}

export const UPDATE_HOMEPAGE_FOOTER = "UPDATE_HOMEPAGE_FOOTER";
export function updateHomepageFooter(footer: HomepageFooter): Action {
	return {
		type: UPDATE_HOMEPAGE_FOOTER,
		payload: footer
	};
}

export const ADD_PAGE_MODULE = "ADD_PAGE_MODULE";
export function addPageModule(page_module: PageModule): Action {
	return {
		type: ADD_PAGE_MODULE,
		payload: page_module
	};
}

export const ADD_POST_REGISTER_HOME_PAGE_MODULE = "ADD_POST_REGISTER_HOME_PAGE_MODULE";
export function addPostRegisterHomePageModule(token: string, homepage: number, page_module: PageModule): AsyncAction {
	return {
		type: ADD_POST_REGISTER_HOME_PAGE_MODULE,
		promise: AddPostRegisterHomePageModuleToEvent(token, homepage, page_module),
	};
}

export const SET_REGISTRATION_HEADER = "SET_REGISTRATION_HEADER";
export function setRegistrationHeader(text: TranslateString): Action {
	return {
		type: SET_REGISTRATION_HEADER,
		payload: text
	};
}

export const SET_REGISTRATION_DESCRIPTION = "SET_REGISTRATION_DESCRIPTION";
export function setRegistrationDescription(text: TranslateString): Action {
	return {
		type: SET_REGISTRATION_DESCRIPTION,
		payload: text
	};
}

export const SET_REGISTRATION_IMAGE = "SET_REGISTRATION_IMAGE";
export function setRegistrationImage(url: string): Action {
	return {
		type: SET_REGISTRATION_IMAGE,
		payload: url
	};
}

export const SET_REGISTRATION_LOGO = "SET_REGISTRATION_LOGO";
export function setRegistrationLogo(url: string): Action {
	return {
		type: SET_REGISTRATION_LOGO,
		payload: url
	};
}

export const REMOVE_IN_CREATION_PROGRESS_SESSION =
	"REMOVE_IN_CREATION_PROGRESS_SESSION";
export function removeInCreationProgressSession(uid: string | null): Action {
	return {
		type: REMOVE_IN_CREATION_PROGRESS_SESSION,
		payload: uid
	};
}

export const EDIT_SESSION_AGENDA = "EDIT_SESSION_AGENDA";
export function editSessionAgenda(uid: string | null): Action {
	return {
		type: EDIT_SESSION_AGENDA,
		payload: uid
	};
}

export const UPDATE_EDITING_SESSION_BY_UID = "UPDATE_EDITING_SESSION_BY_UID";
export function updateEditingSessionByUid(payload: CreateSession): Action {
	return {
		type: UPDATE_EDITING_SESSION_BY_UID,
		payload
	};
}

export const CLEAR_NEW_EVENT_REQUEST = "CLEAR_NEW_EVENT_REQUEST";
export function clearNewEventRequest(payload: null): Action {
	return {
		type: CLEAR_NEW_EVENT_REQUEST,
		payload
	};
}

export const UPDATE_WORKING_EVENT = "UPDATE_WORKING_EVENT";
export function updateWorkingEvent(event: BrandliveEvent, token: string, shouldRerenderQuill = false): AppThunk {
	return async function(dispatch): Promise<void> {
		try {
			dispatch(isSavingEvent(true));
			shouldRerenderQuill && dispatch(forceQuillRerender(true));

			await SaveEvent(event, token);

			batch(() => {
				dispatch({
					type: UPDATE_WORKING_EVENT,
					payload: event
				});
				dispatch({
					type: UPDATE_EVENTS_NAME,
					payload: event
				});
				dispatch(isSavingEvent(false));
				shouldRerenderQuill && dispatch(forceQuillRerender(false));
			});
		} catch (e) {
			console.error(e);
		}
	};
}

export const MODULE_LAYOUT_UPDATE = 'MODULE_LAYOUT_UPDATE';
export function moduleLayoutUpdate(): Action {
	return {
		type: MODULE_LAYOUT_UPDATE,
		payload: null,
	};
}

export const ADD_WORKING_EVENT_HOMEPAGE = "ADD_WORKING_EVENT_HOMEPAGE";
export function addWorkingEventHomepage(uuid: string, token: string, mulitpleSessions?: 'multi'): AsyncAction {
	return {
		type: ADD_WORKING_EVENT_HOMEPAGE,
		promise: AddHomepageToEvent(uuid, token, mulitpleSessions),
	};
}

export const IS_SAVING = "IS_SAVING";
export function isSavingEvent(isSaving: boolean): Action {
	return {
		type: IS_SAVING,
		payload: isSaving
	};
}

export const SET_AGENDA_IMAGE = "SET_AGENDA_IMAGE";
export function setAgendaImage(url: string | null, session: number): Action {
	return {
		type: SET_AGENDA_IMAGE,
		payload: { url, session },
	};
}

export const PUBLISH_EVENT = "PUBLISH_EVENT";
export function publishEvent(token: string, uuid: string, url: string): AsyncAction {
	return {
		type: PUBLISH_EVENT,
		promise: PublishEvent(token, uuid, url)
	};
}

export const PUBLISH_EVENT_PREVIEW = "PUBLISH_EVENT_PREVIEW";
export function publishEventPreview(token: string, preview_event_uuid: string, base_event_uuid: string, url: string): AsyncAction {
	return {
		type: PUBLISH_EVENT_PREVIEW,
		promise: PublishEventPreview(token, preview_event_uuid, base_event_uuid, url)
	};
}

export const UPDATE_ALL_EVENT_SESSIONS_OVERLAY_CHAT = "UPDATE_ALL_EVENT_SESSIONS_OVERLAY_CHAT";
export function updateAllEventSessionsOverlaChat(isOverlay: boolean): Action {
	return {
		type: UPDATE_ALL_EVENT_SESSIONS_OVERLAY_CHAT,
		payload: isOverlay,
	};
}

export const UPDATE_ALL_EVENT_SESSIONS_LAYOUT_TABS = "UPDATE_ALL_EVENT_SESSIONS_LAYOUT_TABS";
export function updateAllEventSessionsLayoutTabs(layout: SessionTabsLayout): Action {
	return {
		type: UPDATE_ALL_EVENT_SESSIONS_LAYOUT_TABS,
		payload: layout,
	};
}

export const UPDATE_WORKING_EVENT_SESSION = "UPDATE_WORKING_EVENT_SESSION";
export function updateWorkingEventSession(session: Session): Action {
	return {
		type: UPDATE_WORKING_EVENT_SESSION,
		payload: session,
	};
}

export const ADD_REGISTRATION_STEP = "ADD_REGISTRATION_STEP";
export function addRegistrationStep(step: RegistrationStep): Action {
	return {
		type: ADD_REGISTRATION_STEP,
		payload: step
	};
}

export const ADD_TICKETING_SET = "ADD_TICKETING_SET";
export function addTicketingSet(set: TicketingSet): Action {
	return {
		type: ADD_TICKETING_SET,
		payload: set
	};
}

export const REMOVE_TICKETING_SET = "REMOVE_TICKETING_SET";
export function removeTicketingSet(index: number): Action {
	return {
		type: REMOVE_TICKETING_SET,
		payload: index
	};
}

export const UPDATE_TICKETING_SET = "UPDATE_TICKETING_SET";
export function updateTicketingSet(set: TicketingSet, index: number): Action {
	return {
		type: UPDATE_TICKETING_SET,
		payload: { set, index }
	};
}

export const GET_GLOBAL_CURRENCIES = "GET_GLOBAL_CURRENCIES";
export function getGlobalCurrencies(token: string): AsyncAction {
	return {
		type: GET_GLOBAL_CURRENCIES,
		promise: GetGlobalCurrencies(token)
	};
}

export const SET_EDITOR_SIZE = "SET_EDITOR_SIZE";
export function setEditorSize(size: 'desktop' | 'tablet' | 'mobile'): Action {
	return {
		type: SET_EDITOR_SIZE,
		payload: size
	};
}

export const TOGGLE_PREVIEW_MODE = "TOGGLE_PREVIEW_MODE";
export function toggleEventPreviewMode(previewMode: string | null): Action {
	return {
		type: TOGGLE_PREVIEW_MODE,
		payload: previewMode
	};
}

export const SET_EVENT_CUSTOM_CSS = "SET_EVENT_CUSTOM_CSS";
export function setEventCustomCss(css: string): Action {
	return {
		type: SET_EVENT_CUSTOM_CSS,
		payload: css
	};
}

export const SET_EVENT_CUSTOM_COMPILED_CSS = "SET_EVENT_CUSTOM_COMPILED_CSS";
export function setEventCustomCompiledCss(css?: string): Action {
	return {
		type: SET_EVENT_CUSTOM_COMPILED_CSS,
		payload: css
	};
}

export const SET_EVENT_USE_SAFE_CSS = "SET_EVENT_USE_SAFE_CSS";
export function setEventUseSafeCss(on: boolean): Action {
	return {
		type: SET_EVENT_USE_SAFE_CSS,
		payload: on
	};
}

export const SET_SOCIAL_SHARING = "SET_SOCIAL_SHARING";
export function setSocialSharing(socialSharing: { image: string, description: string }): Action {
	return {
		type: SET_SOCIAL_SHARING,
		payload: socialSharing
	};
}

export const GET_PUBLISHED_EVENT_URL = "GET_PUBLISHED_EVENT_URL";
export function getPublishedEventUrl(token: string, eventUuid: string): AsyncAction {
	return {
		type: GET_PUBLISHED_EVENT_URL,
		promise: CheckEventPublishedStatus(token, eventUuid)
	};
}

export const UPDATE_PUBLISHED_EVENT_URL = "UPDATE_PUBLISHED_EVENT_URL";
export function updatePublishedUrl(url: string): Action {
	return {
		type: UPDATE_PUBLISHED_EVENT_URL,
		payload: url
	};
}

export const UPDATE_EVENT_FAVICON = "UPDATE_EVENT_FAVICON";
export function updateEventFavicon(token: string, url: string, event_uuid: string): AsyncAction {
	return {
		type: UPDATE_EVENT_FAVICON,
		promise: UpdateEventFavicon(token, url, event_uuid),
	};
}

export const DELETE_EVENT_FAVICON = "DELETE_EVENT_FAVICON";
export function deleteEventFavicon(token: string, event_uuid: string): AsyncAction {
	return {
		type: UPDATE_EVENT_FAVICON,
		promise: DeleteEventFavicon(token, event_uuid),
	};
}

export const UPDATE_CURRENT_POST_REGISTER_PAGE = "UPDATE_CURRENT_POST_REGISTER_PAGE";
export function updateCurrentPostRegisterPage(page: string | null): Action {
	return {
		type: UPDATE_CURRENT_POST_REGISTER_PAGE,
		payload: page
	};
}

export const TOGGLE_EMAIL_SETTINGS_HIDE_BRANDING = "TOGGLE_EMAIL_SETTINGS_HIDE_BRANDING";
export function toggleEmailSettingsHideBranding(shouldHide: boolean): Action {
	return {
		type: TOGGLE_EMAIL_SETTINGS_HIDE_BRANDING,
		payload: shouldHide
	};
}

export const UPDATE_EVENT_NAVBAR = "UPDATE_EVENT_NAVBAR";
export function updateEventNavbar(navbar: EventNavbar): Action {
	return {
		type: UPDATE_EVENT_NAVBAR,
		payload: navbar,
	};
}

export const TOGGLE_DISPLAY_SESSION_FOOTER = "TOGGLE_DISPLAY_SESSION_FOOTER";
export function toggleDisplaySessionFooter(isOn: boolean): Action {
	return {
		type: TOGGLE_DISPLAY_SESSION_FOOTER,
		payload: isOn
	};
}

export const TOGGLE_DISPLAY_SESSIONS = "TOGGLE_DISPLAY_SESSIONS";
export function toggleDisplaySessions(isOn: boolean): Action {
	return {
		type: TOGGLE_DISPLAY_SESSIONS,
		payload: isOn
	};
}


export const TOGGLE_DISPLAY_LANDING_PAGE = "TOGGLE_DISPLAY_LANDING_PAGE";
export function toggleDisplayLandingPage(isOn: boolean): Action {
	return {
		type: TOGGLE_DISPLAY_LANDING_PAGE,
		payload: isOn
	};
}

export const TOGGLE_DISPLAY_DIRECTORY = "TOGGLE_DISPLAY_DIRECTORY";
export function toggleDisplayDirectory(isOn: boolean): Action {
	return {
		type: TOGGLE_DISPLAY_DIRECTORY,
		payload: isOn
	};
}

export const TOGGLE_DISPLAY_PROFILES = "TOGGLE_DISPLAY_PROFILES";
export function toggleDisplayProfiles(isOn: boolean): Action {
	return {
		type: TOGGLE_DISPLAY_PROFILES,
		payload: isOn
	};
}

export const TOGGLE_DISPLAY_AVATAR = "TOGGLE_DISPLAY_AVATAR";
export function toggleDisplayAvatar(isOn: boolean): Action {
	return {
		type: TOGGLE_DISPLAY_AVATAR,
		payload: isOn
	};
}

export const TOGGLE_PROFILE_APPEARANCE = "TOGGLE_PROFILE_APPEARANCE";
export function toggleProfileAppearance(appearance: ProfileDisplayType): Action {
	return {
		type: TOGGLE_PROFILE_APPEARANCE,
		payload: appearance
	};
}

export const TOGGLE_BOOKMARK_ATTENDEES = "TOGGLE_BOOKMARK_ATTENDEES";
export function toggleBookmarkAttendees(isOn: boolean): Action {
	return {
		type: TOGGLE_BOOKMARK_ATTENDEES,
		payload: isOn
	};
}

export const TOGGLE_FILTERS = "TOGGLE_FILTERS";
export function toggleFilters(isOn: boolean): Action {
	return {
		type: TOGGLE_FILTERS,
		payload: isOn
	};
}

export const SET_FILTERS = "SET_FILTERS";
export function setFilters(filters: Array<DirectoryFilter>): Action {
	return {
		type: SET_FILTERS,
		payload: filters
	};
}

export const TOGGLE_MESSAGING = "TOGGLE_MESSAGING";
export function toggleMessaging(isOn: boolean): Action {
	return {
		type: TOGGLE_MESSAGING,
		payload: isOn
	};
}

export const ADD_CUSTOM_PAGE = "ADD_CUSTOM_PAGE";
export function addCustomPage(page: CustomPage): Action {
	return {
		type: ADD_CUSTOM_PAGE,
		payload: page
	};
}

export const UPDATE_CUSTOM_PAGES = "UPDATE_CUSTOM_PAGES";
export function updateCustomPages(customPages: CustomPage[]): Action {
	return {
		type: UPDATE_CUSTOM_PAGES,
		payload: customPages
	};
}

export const UPDATE_CUSTOM_PAGE = "UPDATE_CUSTOM_PAGE";
export function updateCustomPage(customPage: CustomPage): Action {
	return {
		type: UPDATE_CUSTOM_PAGE,
		payload: customPage
	};
}

export const DELETE_CUSTOM_PAGE = "DELETE_CUSTOM_PAGE";
export function deleteCustomPage(customPage: CustomPage, token: string): AsyncAction {
	return {
		type: DELETE_CUSTOM_PAGE,
		promise: DeleteCustomPage(customPage, token)
	};
}

export const TOGGLE_CUSTOM_PAGE_MODULE = "TOGGLE_CUSTOM_PAGE_MODULE";
export function toggleCustomPageModule(pageId: number, moduleId: number, is_on: boolean): Action {
	return {
		type: TOGGLE_CUSTOM_PAGE_MODULE,
		payload: { pageId, moduleId, is_on },
	};
}

export const UPDATE_CUSTOM_PAGE_MODULE = "UPDATE_CUSTOM_PAGE_MODULE";
export function updateCustomPageModule(pageId: number, updatedModule: PageModule): Action {
	return {
		type: UPDATE_CUSTOM_PAGE_MODULE,
		payload: { pageId, updatedModule },
	};
}

export const SET_ADD_MODULE_MODAL_OPEN = "SET_ADD_MODULE_MODAL_OPEN";
export function setAddModuleModalOpen(isOpen: boolean): Action {
	return {
		type: SET_ADD_MODULE_MODAL_OPEN,
		payload: isOpen
	};
}

// ??? should we rename this since we are also using it to update whenever changes are made?
export const SET_SOCIAL_SETTINGS = "SET_SOCIAL_SETTINGS";
export function setSocialSettings(data: SocialSettings, eventUuid: string, token: string): AsyncAction {
	return {
		type: SET_SOCIAL_SETTINGS,
		promise: SetSocialSettings(data, eventUuid, token),
	};
}

export const SET_SOCIAL_IN_STORE_ONLY = "SET_SOCIAL_IN_STORE_ONLY";
export function setSocialsInStoreOnly(data: SocialSettings): Action {
	return {
		type: SET_SOCIAL_IN_STORE_ONLY,
		payload: data,
	};
}

export const RESET_REGISTRATIONS = "RESET_REGISTRATIONS";
export function resetRegistrations(eventId: number, token: string): AsyncAction {
	return {
		type: RESET_REGISTRATIONS,
		promise: ResetRegistrations(eventId, token)
	};
}

export const TOGGLE_TOPIC_GROUP_CHATS = "TOGGLE_TOPIC_GROUP_CHATS";
export function toggleTopicGroupChats(isOn: boolean): Action {
	return {
		type: TOGGLE_TOPIC_GROUP_CHATS,
		payload: isOn
	};
}

export const CREATE_EVENT_CHAT_CHANNEL = "CREATE_EVENT_CHAT_CHANNEL";
export function createEventChatChannel(newChannel: IEventChatChannel): Action {
	return {
		type: CREATE_EVENT_CHAT_CHANNEL,
		payload: newChannel
	};
}

export const TOGGLE_RECAPTCHA = "TOGGLE_RECAPTCHA";
export function toggleRecaptcha(isOn: boolean): Action {
	return {
		type: TOGGLE_RECAPTCHA,
		payload: isOn
	};
}

export const TOGGLE_PERSONALIZED_ATTENDEE_LIST = "TOGGLE_PERSONALIZED_ATTENDEE_LIST";
export function togglePersonalizedAttendeeList(isOn: boolean): Action {
	return {
		type: TOGGLE_PERSONALIZED_ATTENDEE_LIST,
		payload: isOn
	};
}

export const TOGGLE_SHOW_ADD_TO_CAL_BTN = "TOGGLE_SHOW_ADD_TO_CAL_BTN";
export function toggleShowAddToCalBtn(isOn: boolean): Action {
	return {
		type: TOGGLE_SHOW_ADD_TO_CAL_BTN,
		payload: isOn
	};
}

export const TOGGLE_DISPLAY_SESSION_LANGUAGE_DROPDOWN = "TOGGLE_DISPLAY_SESSION_LANGUAGE_DROPDOWN";
export function toggleDisplaySessionLanguageDropdown(isOn: boolean): Action {
	return {
		type: TOGGLE_DISPLAY_SESSION_LANGUAGE_DROPDOWN,
		payload: isOn
	};
}

export const TOGGLE_TRACK_MODAL = "TOGGLE_TRACK_MODAL";
export function toggleAttendeeTrackModal(isOn: boolean): Action {
	return {
		type: TOGGLE_TRACK_MODAL,
		payload: isOn
	};
}

export const SET_VOD_ORDER = "SET_VOD_ORDER";
export function setVodOrder(order: number[]): Action {
	return {
		type: SET_VOD_ORDER,
		payload: order
	};
}

export const TOGGLE_DISPLAY_LIVE_EVENT_BANNER = "TOGGLE_DISPLAY_LIVE_EVENT_BANNER";
export function toggleDisplayLiveEventBanner(display: boolean): Action {
	return {
		type: TOGGLE_DISPLAY_LIVE_EVENT_BANNER,
		payload: display
	};
}

export const TOGGLE_CLOSE_REGISTRATION = "TOGGLE_CLOSE_REGISTRATION";
export function toggleCloseRegistration(isOn: boolean): Action {
	return {
		type: TOGGLE_CLOSE_REGISTRATION,
		payload: isOn
	};
}

export const FORCE_QUILL_RERENDER = "FORCE_QUILL_RERENDER";
export function forceQuillRerender(isUpdating: boolean): Action {
	return {
		type: FORCE_QUILL_RERENDER,
		payload: isUpdating
	};
}

export const ADMIN_TOGGLE_IN_PERSON_ATTENDEE_MODE = "ADMIN_TOGGLE_IN_PERSON_ATTENDEE_MODE";
export function toggleInPersonAttendeeMode(isOn: boolean): Action {
	return {
		type: ADMIN_TOGGLE_IN_PERSON_ATTENDEE_MODE,
		payload: isOn
	};
}

export const CREATE_SCHEDULED_EMAIL = "CREATE_SCHEDULED_EMAIL";
export function createScheduledEmail(token: string, email: IInsertScheduledEmailRequest): AsyncAction {
	return {
		type: CREATE_SCHEDULED_EMAIL,
		promise: CreateScheduledEmail(token, email)
	};
}

export const EDIT_SCHEDULED_EMAIL = "EDIT_SCHEDULED_EMAIL";
export function editScheduledEmail(token: string, email: IScheduledEmail): AsyncAction {
	return {
		type: EDIT_SCHEDULED_EMAIL,
		promise: EditScheduledEmail(token, email)
	};
}

export const EDIT_SCHEDULED_EMAIL_IN_ADMIN = "EDIT_SCHEDULED_EMAIL_IN_ADMIN";
export function editScheduledEmailInAdmin(email: IScheduledEmail): Action {
	return {
		type: EDIT_SCHEDULED_EMAIL_IN_ADMIN,
		payload: email
	};
}

export const DELETE_SCHEDULED_EMAIL = "DELETE_SCHEDULED_EMAIL";
export function deleteScheduledEmail(token: string, scheduleUuid: IScheduledEmail['uuid']): AsyncAction {
	return {
		type: DELETE_SCHEDULED_EMAIL,
		promise: DeleteScheduledEmail(token, scheduleUuid)
	};
}
export const GET_EVENT_SCHEDULED_EMAILS = "GET_EVENT_SCHEDULED_EMAILS";
export function getEventScheduledEmails(token: string, event_uuid: string): AsyncAction {
	return {
		type: GET_EVENT_SCHEDULED_EMAILS,
		promise: GetEventScheduledEmails(token, event_uuid)
	};
}

export const GET_CHANNEL_SCHEDULED_EMAILS = "GET_CHANNEL_SCHEDULED_EMAILS";
export function getChannelScheduledEmails(token: string, channel: number): AsyncAction {
	return {
		type: GET_CHANNEL_SCHEDULED_EMAILS,
		promise: GetChannelScheduledEmails(token, channel)
	};
}

export const CLEAR_CHANNEL_SCHEDULED_EMAILS = "CLEAR_CHANNEL_SCHEDULED_EMAILS";
export function clearChannelScheduledEmails(): Action {
	return {
		type: CLEAR_CHANNEL_SCHEDULED_EMAILS,
		payload: null,
	};
}

export const SET_DISPLAY_SCHEDULED_EMAIL = "SET_DISPLAY_SCHEDULED_EMAIL";
export function setDisplayScheduledEmail(scheduledEmailUuid: IScheduledEmail['uuid']): Action {
	return {
		type: SET_DISPLAY_SCHEDULED_EMAIL,
		payload: scheduledEmailUuid
	};
}

export const CLEAR_SCHEDULED_EMAILS = "CLEAR_SCHEDULED_EMAILS";
export function clearScheduledEmails(): Action {
	return {
		type: CLEAR_SCHEDULED_EMAILS,
		payload: null
	};
}

export const TOGGLE_TRANSLATIONS_V2 = "TOGGLE_TRANSLATIONS_V2";
export function toggleTranslationsV2(on: boolean): Action {
	return {
		type: TOGGLE_TRANSLATIONS_V2,
		payload: on
	};
}

export const RE_RENDER_QUILL = "RE_RENDER_QUILL";
export function reRenderQuill(): Action {
	return {
		type: RE_RENDER_QUILL,
		payload: null
	};
}

export const SET_TRANSLATION_MANIFESTS = "SET_TRANSLATION_MANIFESTS";
export function setTranslationManifests(manifests: Record<string, string>) {
	return {
		type: SET_TRANSLATION_MANIFESTS,
		payload: manifests
	};
}

export const SETUP_EVENT_PREVIEW = "SETUP_EVENT_PREVIEW";
export function setupEventPreview(token: string, eventUuid: string, channel: number) {
	return {
		type: SETUP_EVENT_PREVIEW,
		promise: SetupEventPreview(token, eventUuid, channel)
	};
}

export const UPDATE_EVENT_PREVIEW_PASSWORD = "UPDATE_EVENT_PREVIEW_PASSWORD";
export function updateEventPreviewPassword(token: string, eventPreviewUuid: string) {
	return {
		type: UPDATE_EVENT_PREVIEW_PASSWORD,
		promise: UpdateEventPreviewPassword(eventPreviewUuid, token)
	};
}

export const SET_EVENT_TAGS = "SET_EVENT_TAGS";
export function setEventTags(tags: IEventTag[]): Action {
	return {
		type: SET_EVENT_TAGS,
		payload: tags,
	};
}

export const TOGGLE_AUTOMATIC_SESSION_FILTER_BY_USER_LANGUAGE = "TOGGLE_AUTOMATIC_SESSION_FILTER_BY_USER_LANGUAGE ";
export function toggleAutomaticSessionFilterByUserLanguage(isOn: boolean): Action {
	return {
		type: TOGGLE_AUTOMATIC_SESSION_FILTER_BY_USER_LANGUAGE,
		payload: isOn
	};
}

export const UPDATE_CHANNEL_ICON = "UPDATE_CHANNEL_ICON";
export function updateChannelIcon(channelIcons: ChannelCustomizedIcon[]) {
	return {
		type: UPDATE_CHANNEL_ICON,
		payload: channelIcons,
	};
}
export const SET_EMAIL_LINK_LIMIT = "SET_EMAIL_LINK_LIMIT";
export function setEmailLinkLimit(limit: number | null): Action {
	return {
		type: SET_EMAIL_LINK_LIMIT,
		payload: limit
	};
}

export const UPDATE_SESSION_BACKUP_URL = "UPDATE_SESSION_BACKUP_URL";
export function updateSessionBackupUrl(sessionUuid: string, enabled: boolean, url: string): Action {
	return {
		type: UPDATE_SESSION_BACKUP_URL,
		payload: { sessionUuid, enabled, url }
	};
}

export const SET_ACTIVE_ACCORDION_CONTAINER = "SET_ACTIVE_ACCORDION_CONTAINER";
export function setActiveAccordionContainer(containerId: UniqueIdentifier | null): Action {
	return {
		type: SET_ACTIVE_ACCORDION_CONTAINER,
		payload: containerId,
	};
}

export const ADD_LEADERBOARD_NAV_ITEM = 'ADD_LEADERBOARD_NAV_ITEM';
export function addLeaderboardNavItem(token: string, eventUuid: string, homepageId: number): AsyncAction {
	return {
		type: ADD_LEADERBOARD_NAV_ITEM,
		promise: AddLeaderboardNavItem(token, eventUuid, homepageId),
	};
}

export const UPDATE_SESSION_FEEDBACK_SURVEY_TEXT = "UPDATE_SESSION_FEEDBACK_SURVEY_TEXT";
export function updateSessionFeedbackSurveyText(token: string, eventUuid: string, feedbackSurveyText: IFeedbackSurveyText, languages: string[]): AsyncAction {
	return {
		type: UPDATE_SESSION_FEEDBACK_SURVEY_TEXT,
		promise: UpdateSessionFeedbackSurveyText(token, eventUuid, feedbackSurveyText, languages)
	};
}

export const SET_MARKETING_OPT_IN_TYPE = "SET_MARKETING_OPT_IN_TYPE";
export function setMarketingOptInType(type: MarketingOptInTypes): Action {
	return {
		type: SET_MARKETING_OPT_IN_TYPE,
		payload: type
	};
}
